import { useEffect, useMemo } from 'react'
import { Modal, Alert, Spinner } from 'react-bootstrap'
import DateTimeComp from '../../../components/dateComponent/DateTimeComp'
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp'
import labelKey from '../../localization/label.json'
import { constraintConfig } from '../../../constraintConfig'
import {
    useDepartmentsLookupQuery,
    useGetAllDesignationWithoutRoleQuery,
    useGetAllDivisionQuery,
    useGetAllLocationsQuery,
    useGetAllLst_UserExpenseTypeQuery,
    useGetAllTimeZoneQuery,
    useGetAllUserTypeLookupQuery,
} from '../../../services/GeneralApi'
import { useGetUserDraftByIdQuery } from '../../../services/DraftApi'
import { useGetSingleUserQuery } from '../../../services/authApi'
import { useAppSelector } from '../../../hooks'
import { selectAuth } from '../../../features/authSlice'

type Props = {
    show: boolean
    handleClose: () => void
    data: any
}

const FALLBACK_EXPENSE_ACCOUNT_TYPE_OPTIONS = [
    { id: 1, value: 'Flex' },
    { id: 2, value: 'Regular' },
    { id: 3, value: 'Both (Flex & Regular)' },
]

const Field = ({ label, children, className = 'col-md-4 col-lg-3' }: { label: string; children: any; className?: string }) => (
    <div className={`${className} mb-3`}>
        <label className='fw-semibold text-muted d-block mb-1 fs-7'>{label}</label>
        <div className='text-gray-800 fs-6'>{(children !== null && children !== undefined && children !== '') ? children : 'N/A'}</div>
    </div>
)

const normalizeId = (id: any) => {
    if (id == null || id === '' || Number(id) === 0) return null
    return id
}

const lookupLabel = (list: any[] | undefined, id: any, fallbacks?: { id: number; value: string }[]) => {
    const key = normalizeId(id)
    if (key == null) return 'N/A'
    const found = (list || []).find((o: any) => String(o.id) === String(key))
    if (found) return found.value || found.title || found.name || found.timeZone || String(key)
    const fb = (fallbacks || []).find(o => String(o.id) === String(key))
    if (fb) return fb.value
    return String(key)
}

const OnboardingUserRequestDetailModal = ({ show, handleClose, data }: Props) => {
    const { isTravelModuleAccessAvailable } = useAppSelector(selectAuth)
    const draftId = Number(data?.userDraftID || data?.id || 0)
    const {
        data: draftResponse,
        isFetching,
        isError,
        refetch,
    } = useGetUserDraftByIdQuery(draftId, {
        skip: !show || !draftId,
        refetchOnMountOrArgChange: true,
    })

    useEffect(() => {
        if (show && draftId) refetch()
    }, [show, draftId, refetch])

    const detail = draftResponse?.result || null

    const { data: departmentLookup } = useDepartmentsLookupQuery('', { skip: !show })
    const { data: designationLookup } = useGetAllDesignationWithoutRoleQuery('', { skip: !show })
    const { data: locationsData } = useGetAllLocationsQuery('', { skip: !show })
    const { data: divisionLookup } = useGetAllDivisionQuery('', { skip: !show })
    const { data: timeZoneLookup } = useGetAllTimeZoneQuery('', { skip: !show })
    const { data: userTypeLookup } = useGetAllUserTypeLookupQuery('', { skip: !show })
    const { data: expenseTypeLookup } = useGetAllLst_UserExpenseTypeQuery('', { skip: !show })

    const managerId = normalizeId(detail?.managerId)
    const { data: managerUser } = useGetSingleUserQuery(managerId, {
        skip: !show || !managerId,
    })

    const managerDisplay = useMemo(() => {
        const email = detail?.managerEmail
        const nameFromApi = detail?.managerName?.trim()
        const mgr = managerUser?.result
        if (mgr) {
            const name = `${mgr.firstName || ''} ${mgr.lastName || ''}`.trim() || nameFromApi
            const mgrEmail = mgr.email || email
            if (name && mgrEmail) return `${name} (${mgrEmail})`
            if (name) return name
            if (mgrEmail) return mgrEmail
        }
        if (nameFromApi && email) return `${nameFromApi} (${email})`
        if (nameFromApi) return nameFromApi
        if (email) return email
        if (managerId) return `Manager #${managerId}`
        return 'N/A'
    }, [managerUser, detail?.managerEmail, detail?.managerName, managerId])

    const statusId = detail?.userApprovalStatus?.id
    const statusLabel =
        String(statusId) === constraintConfig.newUserRequestStatus.Approved
            ? labelKey.approved
            : String(statusId) === constraintConfig.newUserRequestStatus.Pending
                ? labelKey.pendingForApproval
                : labelKey.rejected

    const statusBadge =
        String(statusId) === constraintConfig.newUserRequestStatus.Approved
            ? 'badge badge-light-success fs-7 fw-semibold'
            : String(statusId) === constraintConfig.newUserRequestStatus.Pending
                ? 'badge badge-light-info fs-7 fw-semibold'
                : 'badge badge-light-danger fs-7 fw-semibold'

    const departmentId = normalizeId(detail?.department?.id ?? detail?.departmentId)
    const designationId = normalizeId(detail?.designation?.id ?? detail?.designationId)
    const locationId = normalizeId(detail?.location?.id ?? detail?.locationId)
    const divisionId = normalizeId(detail?.division?.id ?? detail?.divisionId)
    const timeZoneId = normalizeId(detail?.userTimeZone?.id ?? detail?.userTimeZoneId)
    const userTypeId = normalizeId(detail?.userType?.id ?? detail?.userTypeId)
    const accountTypeId = normalizeId(detail?.accountTypeId ?? detail?.expenseType?.id)

    const departmentName = lookupLabel(departmentLookup?.result, departmentId)
    const designationName = lookupLabel(designationLookup?.result, designationId)
    const locationName = lookupLabel(locationsData?.result, locationId)
    const divisionName = lookupLabel(divisionLookup?.result, divisionId)
    const timeZoneName = lookupLabel(timeZoneLookup?.result, timeZoneId)
    const userTypeName = lookupLabel(userTypeLookup?.result, userTypeId, [
        { id: 4, value: 'Manager' },
        { id: 2, value: 'Provider' },
        { id: 5, value: 'Employee' },
        { id: 1, value: 'Regular' },
    ])
    const expenseAccountTypeName = lookupLabel(
        expenseTypeLookup?.result,
        accountTypeId,
        FALLBACK_EXPENSE_ACCOUNT_TYPE_OPTIONS,
    )
    const expenseTypeLabel = (
        expenseTypeLookup?.result || []
    ).find((o: any) => String(o.id) === String(accountTypeId))?.value
        || FALLBACK_EXPENSE_ACCOUNT_TYPE_OPTIONS.find(o => String(o.id) === String(accountTypeId))?.value
        || ''
    const showFlexBudget = /flex/i.test(expenseTypeLabel) || accountTypeId === 1 || accountTypeId === 3

    return (
        <Modal
            className='modal fade'
            tabIndex={-1}
            dialogClassName='modal-dialog modal-dialog-centered mw-1000px'
            show={show}
            onHide={handleClose}
        >
            <div className='modal-header py-3'>
                <h2 className='fw-bolder fs-3 mb-0'>Onboarding User Request Detail</h2>
                <div className='btn btn-icon btn-sm btn-active-icon-primary' onClick={handleClose}>
                    <i className='fas fa-times'></i>
                </div>
            </div>
            <div className='modal-body scroll-y mx-lg-4 my-2 py-3'>
                {isFetching && (
                    <div className='d-flex justify-content-center align-items-center py-10'>
                        <Spinner animation='border' variant='primary' />
                    </div>
                )}
                {!isFetching && isError && (
                    <Alert variant='danger'>Unable to load user request details.</Alert>
                )}
                {!isFetching && !isError && detail && (
                    <>
                        <div className='d-flex align-items-center mb-4'>
                            <div className='symbol symbol-circle symbol-45px overflow-hidden me-3'>
                                <div className='symbol-label fs-3 bg-light-info text-info text-capitalize'>
                                    {detail?.firstName?.charAt(0) || '?'}
                                </div>
                            </div>
                            <div>
                                <div className='fw-bold text-gray-800 text-capitalize fs-4'>
                                    {detail?.firstName} {detail?.lastName}
                                </div>
                                <div className='text-muted text-lowercase'>{detail?.userEmail || 'N/A'}</div>
                            </div>
                        </div>

                        <h5 className='fw-bold text-gray-800 mb-3 border-bottom pb-2'>Account &amp; Status</h5>
                        <div className='row'>
                            <Field label='Status'><span className={statusBadge}>{statusLabel}</span></Field>
                            <Field label='Organization'>{detail?.apiOrgName}</Field>
                            <Field label='Expense Account Type'>{expenseAccountTypeName}</Field>
                            <Field label='User Type'>{userTypeName}</Field>
                            {showFlexBudget && <Field label='User Flex Budget'>{detail?.flexBudget ?? 'N/A'}</Field>}
                            <Field label='Dayforce ID'>{detail?.dayforceId}</Field>
                            <Field label='Requested Date'><DateTimeComp formattedDate={detail?.createdDT} /></Field>
                            <Field label='Approval Date'><DateTimeComp formattedDate={detail?.approvelDT} /></Field>
                            <Field label='Modified On'><DateTimeComp formattedDate={detail?.modifiedOn || detail?.createdDT} /></Field>
                            <Field label='Comments' className='col-md-6 col-lg-6'>{detail?.comments}</Field>
                        </div>

                        <h5 className='fw-bold text-gray-800 mb-3 mt-3 border-bottom pb-2'>Contact &amp; Manager</h5>
                        <div className='row'>
                            <Field label='Manager' className='col-md-6 col-lg-6'>{managerDisplay}</Field>
                            <Field label='Cell Phone'>{detail?.cellPhone}</Field>
                            <Field label='Business Phone'>{detail?.businessPhone}</Field>
                        </div>

                        <h5 className='fw-bold text-gray-800 mb-3 mt-3 border-bottom pb-2'>Org Details</h5>
                        <div className='row'>
                            <Field label='Division'>{divisionName}</Field>
                            <Field label='Department'>{departmentName}</Field>
                            <Field label='Designation'>{designationName}</Field>
                            <Field label='Ship To / Office Location' className='col-md-6 col-lg-6'>{locationName}</Field>
                            <Field label='Time Zone'>{timeZoneName}</Field>
                            {/* <Field label='Expense Policy Limit'>{detail?.expensePolicyLimit ?? 'N/A'}</Field> */}
                        </div>

                        <h5 className='fw-bold text-gray-800 mb-3 mt-3 border-bottom pb-2'>Form/Shipping Address</h5>
                        <div className='row'>
                            <Field label='Find Address' className='col-12 col-lg-6'>{detail?.findAddress}</Field>
                            <Field label='Address Line 1'>{detail?.addressLine1}</Field>
                            <Field label='Address Line 2'>{detail?.addressLine2}</Field>
                            <Field label='City'>{detail?.city}</Field>
                            <Field label='State'>{detail?.state}</Field>
                        </div>

                        <h5 className='fw-bold text-gray-800 mb-3 mt-3 border-bottom pb-2'>Corporate Credit Card Request Detail</h5>
                        <div className='row'>
                            {!!isTravelModuleAccessAvailable && (
                                <Field label='Travel Portal Access'>{detail?.requestTravelPortalAccess ? 'Yes' : 'No'}</Field>
                            )}
                            <Field label='Corporate Credit Card Requested'>{detail?.requestCorporateCreaditCard ? 'Yes' : 'No'}</Field>
                            {!!detail?.requestCorporateCreaditCard && (
                                <Field label='Corporate Credit Card Corp CC Limit'>
                                    {detail?.spendingLimit != null && Number(detail.spendingLimit) > 0
                                        ? <CurrencyComp amount={detail.spendingLimit} />
                                        : 'N/A'}
                                </Field>
                            )}
                        </div>

                        {!!detail?.requestCorporateCreaditCard && (
                            <Alert variant='primary' className='mt-2 mb-0 py-2'>
                                After manager approval, a corporate credit card request is created and routed to higher management by division.
                            </Alert>
                        )}
                    </>
                )}
            </div>
            <div className='modal-footer py-3'>
                <button type='button' className='btn btn-light btn-sm' onClick={handleClose}>
                    Close
                </button>
            </div>
        </Modal>
    )
}

export default OnboardingUserRequestDetailModal
